import { useCallback } from "react";
import Konva from "konva";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { submenuDataAtom, syncServiceAtom, transformerRefAtom, undoRedoStackAtom, utilsAtom } from "state-atoms";
import consts from "shared/consts";
import tracking from "frontend/tracking";
import style from "../../elements-toolbar-cim-restyle/elements-toolbar.module.css";
import popupStyle from "./customize-monday-item.module.css";
import { MondayLogo } from "frontend/icons/monday-logo";
import { useDynamicElementActions } from "../hooks/use-dynamic-element-actions";
import { DynamicElement } from "../dynamic-data-utils";
import { placeStickyNote } from "shared/datamodel/sticky-note";
import { Connector, IntegrationItem, Shape, StickyNote } from "shared/datamodel/schemas";
import { placeShape } from "shared/datamodel/shape";
import { getDefaultSizeForShape } from "utils/shape-utils";
import { CustomizeMondayItemIcons } from "./customize-monday-item-icons";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import { IntegrationType } from "shared/integrations/integration";
import { ElementDataLayer } from "shared/datamodel/schemas/element-data-layer";
import { ReadTransaction } from "@workcanvas/reflect";
import { canvasElementPrefix } from "shared/util/utils";
import { getClosestAnchor } from "frontend/canvas-designer-new/elements/connector/connector-utils";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { placeMondayTask } from "shared/datamodel/monday-task";
import { NewElementData } from "shared/datamodel/canvas-element";
import { Sizes } from "frontend/data/shapes/sizes";
import { DropdownButton } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";

function CustomizeMondayItemPopup({ node, onSelect }: { node: Konva.Node; onSelect: (target: string) => void }) {
  return (
    <div className={popupStyle.popup}>
      <hgroup className={popupStyle.headerPadding}>
        <h1>
          <MondayLogo size={22} />
          Customize look
        </h1>
        <p>
          Restyle the element,
          <br />
          keep the content
        </p>
      </hgroup>
      <div className={popupStyle.popupIcons}>
        {Object.entries(CustomizeMondayItemIcons).map(([key, icon]) => {
          let isDisabled = false;

          // This component only supports old integration items.
          // The new monday tasks have their own toolbar widget, which simplifies things.
          const isIntegrationItem = node.attrs.type === consts.CANVAS_ELEMENTS.INTEGRATION;
          if (key === "integrationItem" && !isIntegrationItem) {
            const integrationCount = Object.values(node.attrs.element.dataLayer?.integrations || {}).reduce(
              (acc: number, integration) => acc + Object.keys(integration ?? {}).length,
              0
            );
            isDisabled = integrationCount > 1;
          }

          return (
            <button
              className={classNames(popupStyle.popupIcon, { [popupStyle.disabled]: isDisabled })}
              key={key}
              onClick={() => onSelect(key)}
              disabled={isDisabled}
            >
              {icon}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CustomizeMondayItemButton({ node, documentId }: { node: Konva.Node; documentId: string }) {
  const syncService = useAtomValue(syncServiceAtom);
  const reflect = syncService?.getReplicache();
  const submenuData = useAtomValue(submenuDataAtom);
  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const transformerRef = useAtomValue(transformerRefAtom);
  const { addFieldToElement, addIntegrationToElement, removeIntegrationFromElement, getDerivedTextIds } =
    useDynamicElementActions();

  const newMondayTaskEnabled = useFeatureFlag("new-monday-task-element");

  const migrateConnectors = useCallback(
    async (from: string, to: string, sequenceId?: string) => {
      const connectorIds: string[] =
        (await reflect?.query(async function (tx: ReadTransaction) {
          const prefix = `${canvasElementPrefix}connector-`;
          let allConnectors = (await tx.scan({ prefix }).entries().toArray()) as [string, Connector][];
          allConnectors = allConnectors.filter(([_, connector]) => {
            if (connector?.hidden) return false;

            const [source, destination] = connector.connectedShapes;
            if (source?.id === from || destination?.id === from) return true;
            return false;
          });

          const ids = allConnectors.map(([id]) => id.replace(canvasElementPrefix, ""));
          return ids;
        })) ?? [];

      await undoRedoStack?.patchElement(
        from,
        (element) => {
          const { attachedConnectors } = element;
          if (!attachedConnectors) return;

          for (const connector of connectorIds) {
            delete attachedConnectors[connector];
          }
        },
        sequenceId
      );

      await undoRedoStack?.patchElement(
        to,
        (element) => {
          const { attachedConnectors } = element;
          if (!attachedConnectors) return;

          for (const connector of connectorIds) {
            attachedConnectors[connector] = {
              lineId: connector,
            };
          }
        },
        sequenceId
      );

      await undoRedoStack?.patchElements(
        connectorIds,
        (connector: Connector) => {
          const [source, destination] = connector.connectedShapes;
          if (source?.id === from) {
            source.id = to;
          }
          if (destination?.id === from) {
            destination.id = to;
          }

          // Migrate anchors mode for integration items:
          // Old monday items: Convert to anchor, exclude bottom
          // New monday items: Convert to anchor, include bottom
          const allowBottomAnchor = newMondayTaskEnabled;
          const elementType = to.split("-")[0];
          if (
            elementType === consts.CANVAS_ELEMENTS.INTEGRATION ||
            elementType === consts.CANVAS_ELEMENTS.MONDAY_TASK
          ) {
            if (!connector.anchorsMode) return;

            for (const [index, mode] of connector.anchorsMode.entries()) {
              // Typo on purpose, legacy code
              if ((mode === "buttom" || mode === "bottom") && !allowBottomAnchor) {
                // Since bottom us not supported, we default to top
                connector.anchorsMode[index] = "top";
              } else if (mode === "outline") {
                const t = index === 0 ? connector.point1_t : connector.point2_t;
                let closestAnchor = getClosestAnchor(t);

                // Typo on purpose, legacy code
                if ((closestAnchor === "buttom" || closestAnchor === "bottom") && !allowBottomAnchor) {
                  closestAnchor = "top";
                }

                connector.anchorsMode[index] = closestAnchor;
              }
            }
          }
        },
        sequenceId
      );
    },
    [reflect, undoRedoStack, newMondayTaskEnabled]
  );

  const handleCreateDataLayerElement = useCallback(
    async (target: string) => {
      const { integrationId, configuration, x, y } = node.attrs.element as IntegrationItem;
      const { itemId } = configuration;

      tracking.trackAnalyticsEvent("data_layer_created_from_monday_item", { integrationId, itemId, target });

      let targetElement: { id: string; type: string; element: Shape | StickyNote };
      if (target === consts.CANVAS_ELEMENTS.STICKY_NOTE) {
        const size = Sizes.get().stickyNote.height;
        const { id, stickyNote } = placeStickyNote(
          { ...submenuData?.stickyNote },
          { x: x + size / 2, y: y + size / 2 },
          Sizes.get().stickyNote
        );
        targetElement = { id, type: consts.CANVAS_ELEMENTS.STICKY_NOTE, element: stickyNote };
      } else {
        const shapeDefaultSize = getDefaultSizeForShape(consts.CANVAS_ELEMENTS.SHAPE, target);
        const { id, shape } = placeShape({
          point: { x: x + shapeDefaultSize.width / 2, y: y + shapeDefaultSize.height / 2 },
          init: { ...submenuData?.shape, type: consts.CANVAS_ELEMENTS.SHAPE, subtype: target },
          actualSize: shapeDefaultSize,
          defaultShapeSize: shapeDefaultSize,
          rotate: 0,
          sizes: {
            fontSize: Sizes.get().shape.fontSize,
            strokeWidth: Sizes.get().shape.strokeWidth.default,
            defaultRadius: Sizes.get().shape.defaultRadius,
          },
        });
        targetElement = { id, type: consts.CANVAS_ELEMENTS.SHAPE, element: shape };
      }

      const sequenceId = `customize-monday-item-${node.attrs.id}`;
      undoRedoStack?.startSequence(sequenceId);

      const newElementIds = await canvasUtils?.onAddElements(
        [targetElement],
        true,
        "customize-monday-item",
        sequenceId
      );

      if (!newElementIds) {
        // Should never happen
        return;
      }

      const targetElementId = newElementIds[0];

      await addIntegrationToElement(targetElementId, integrationId, itemId, {
        clearText: true,
        sequenceId,
      });

      const addedElementId = await addFieldToElement(
        targetElementId,
        targetElement.element as DynamicElement,
        integrationId,
        itemId,
        "name",
        {
          ...submenuData?.textBlock,
          sequenceId,
        }
      );

      if (!addedElementId) {
        // Should never happen
        return;
      }

      await migrateConnectors(node.attrs.id, targetElementId, sequenceId);

      await canvasUtils?.removeCanvasElements([node.attrs.id], true, sequenceId);
      undoRedoStack?.endSequence(sequenceId);
      canvasUtils?.setSelectedElementIds([targetElementId, addedElementId]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canvasUtils, node.attrs.id]
  );

  const replaceDataLayerElement = useCallback(
    async (
      newElement: { id: string; type: string; element: Shape | StickyNote },
      existingDataLayer: ElementDataLayer
    ) => {
      const sequenceId = `customize-monday-item-${node.attrs.id}`;
      undoRedoStack?.startSequence(sequenceId);

      const newElementIds = await canvasUtils?.onAddElements([newElement], true, "customize-monday-item", sequenceId);

      if (!newElementIds) {
        // Should never happen
        return;
      }

      for (const integration of Object.entries(existingDataLayer?.integrations || {})) {
        const [integrationId, configuration] = integration as [string, Record<string, unknown>];
        for (const itemId of Object.keys(configuration)) {
          // eslint-disable-next-line no-await-in-loop
          await removeIntegrationFromElement(node.attrs.id, integrationId, itemId, {
            sequenceId,
          });
          // eslint-disable-next-line no-await-in-loop
          await addIntegrationToElement(newElementIds[0], integrationId, itemId, {
            sequenceId,
          });
        }
      }

      await canvasUtils?.removeCanvasElements([node.attrs.id], true, sequenceId);

      const fields = Object.keys(existingDataLayer?.elementMapping || {});

      for (const field of fields) {
        const [, integrationId, itemId, columnId] = field.split(":");
        // eslint-disable-next-line no-await-in-loop
        await addFieldToElement(
          newElementIds[0],
          newElement.element as DynamicElement,
          integrationId,
          itemId,
          columnId,
          {
            sequenceId,
          }
        );
      }

      await migrateConnectors(node.attrs.id, newElementIds[0], sequenceId);

      undoRedoStack?.endSequence(sequenceId);

      const derivedTextIds = await getDerivedTextIds(newElementIds[0]);
      canvasUtils?.setSelectedElementIds([...newElementIds, ...derivedTextIds]);
    },
    [
      node.attrs.id,
      undoRedoStack,
      canvasUtils,
      migrateConnectors,
      getDerivedTextIds,
      removeIntegrationFromElement,
      addIntegrationToElement,
      addFieldToElement,
    ]
  );

  const handleCreateIntegrationItem = useCallback(async () => {
    const { type, x, y, width, height, dataLayer } = node.attrs.element;

    // We assume that the data layer element only has one integration.
    const integration = Object.entries(dataLayer?.integrations || {})[0];
    if (!integration) return;

    const [integrationId, configuration] = integration as [string, Record<string, unknown>];
    const itemId = Object.keys(configuration)[0];

    if (!itemId) return;

    const point =
      type === consts.CANVAS_ELEMENTS.SHAPE
        ? {
            x: x - width / 2,
            y: y - height / 2,
          }
        : { x, y };

    let data: NewElementData;
    if (newMondayTaskEnabled) {
      const newElement = placeMondayTask(point, { mondayItemId: itemId, integrationId });
      data = {
        id: newElement.id,
        type: newElement.mondayTask.type,
        element: newElement.mondayTask,
      };
    } else {
      const newElement = placeIntegrationElement(point, integrationId, IntegrationType.monday, { itemId });
      data = {
        id: newElement.id,
        type: newElement.type,
        element: newElement.item,
      };
    }

    const sequenceId = `customize-monday-item-${node.attrs.id}`;
    undoRedoStack?.startSequence(sequenceId);

    const newElementIds = await canvasUtils?.onAddElements([data], true, "customize-monday-item", sequenceId);

    if (!newElementIds) {
      // Should never happen
      return;
    }

    // Remove the derived texts from the data layer element
    await removeIntegrationFromElement(node.attrs.id, integrationId, itemId, {
      sequenceId,
    });

    await canvasUtils?.removeCanvasElements([node.attrs.id], true, sequenceId);

    await migrateConnectors(node.attrs.id, newElementIds[0], sequenceId);

    undoRedoStack?.endSequence(sequenceId);
    canvasUtils?.setSelectedElementIds(newElementIds);
  }, [
    canvasUtils,
    migrateConnectors,
    node.attrs.element,
    node.attrs.id,
    removeIntegrationFromElement,
    undoRedoStack,
    newMondayTaskEnabled,
  ]);

  const handleChangeStickyNoteToShape = useCallback(
    async (target: string) => {
      const { x, y, dataLayer } = node.attrs.element;
      const shapeDefaultSize = getDefaultSizeForShape(consts.CANVAS_ELEMENTS.SHAPE, target);
      const { id, shape } = placeShape({
        point: { x: x + shapeDefaultSize.width / 2, y: y + shapeDefaultSize.height / 2 },
        init: { ...submenuData?.shape, type: consts.CANVAS_ELEMENTS.SHAPE, subtype: target },
        actualSize: shapeDefaultSize,
        defaultShapeSize: shapeDefaultSize,
        rotate: 0,
        sizes: {
          fontSize: Sizes.get().shape.fontSize,
          strokeWidth: Sizes.get().shape.strokeWidth.default,
          defaultRadius: Sizes.get().shape.defaultRadius,
        },
      });
      const newElement = { id, type: consts.CANVAS_ELEMENTS.SHAPE, element: shape };
      await replaceDataLayerElement(newElement, dataLayer);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [node.attrs.element, replaceDataLayerElement]
  );

  const handleChangeShapeToStickyNote = useCallback(async () => {
    const { x, y, dataLayer } = node.attrs.element;
    const { id, stickyNote } = placeStickyNote({ ...submenuData?.stickyNote }, { x, y }, Sizes.get().stickyNote);
    const newElement = { id, type: consts.CANVAS_ELEMENTS.STICKY_NOTE, element: stickyNote };
    await replaceDataLayerElement(newElement, dataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.attrs.element, replaceDataLayerElement]);

  const handleChangeDataLayerElement = useCallback(
    async (target: string) => {
      const fromType = node.attrs.type;
      const toType =
        target === consts.CANVAS_ELEMENTS.STICKY_NOTE
          ? consts.CANVAS_ELEMENTS.STICKY_NOTE
          : consts.CANVAS_ELEMENTS.SHAPE;

      if (fromType === consts.CANVAS_ELEMENTS.STICKY_NOTE) {
        if (toType === consts.CANVAS_ELEMENTS.SHAPE) {
          await handleChangeStickyNoteToShape(target);
        }
      } else {
        if (toType === consts.CANVAS_ELEMENTS.STICKY_NOTE) {
          await handleChangeShapeToStickyNote();
        } else {
          undoRedoStack?.patchElement(node.attrs.id, (element) => {
            (element as Shape).subtype = target;
          });
          const derivedTextIds = await getDerivedTextIds(node.attrs.id);
          canvasUtils?.setSelectedElementIds([node.attrs.id, ...derivedTextIds]);
        }
      }
    },
    [
      canvasUtils,
      getDerivedTextIds,
      handleChangeShapeToStickyNote,
      handleChangeStickyNoteToShape,
      node.attrs.id,
      node.attrs.type,
      undoRedoStack,
    ]
  );

  const handleClick = useCallback(
    async (target: string) => {
      const fromType = node.attrs.type === "integrationItem" ? "integrationItem" : "dataLayer";
      const toType = target === "integrationItem" ? "integrationItem" : "dataLayer";

      if (fromType === "integrationItem") {
        if (toType === "dataLayer") {
          await handleCreateDataLayerElement(target);
        }
      } else {
        if (toType === "integrationItem") {
          await handleCreateIntegrationItem();
        } else {
          await handleChangeDataLayerElement(target);
        }
      }

      // In some cases the transformer is not updated properly,
      // we combat this by adding a timeout to enforce the update
      setTimeout(() => {
        transformerRef?.current?.forceUpdate();
      }, 100);
    },
    [
      handleCreateDataLayerElement,
      handleCreateIntegrationItem,
      handleChangeDataLayerElement,
      node.attrs.type,
      transformerRef,
    ]
  );

  return (
    <div className={style.row}>
      <DropdownButton
        data-testid="customizeMondayItem"
        tooltip="Customize look"
        hideArrow
        renderPopup={() => <CustomizeMondayItemPopup node={node} onSelect={handleClick} />}
      >
        <div className={style.convertToMondayButtonContent}>
          <MondayLogo size={22} />
          <span>Customize</span>
        </div>
      </DropdownButton>
    </div>
  );
}
